import { StyleSheet, View, ScrollView } from 'react-native';
import { Text, Button, Surface, useTheme } from 'react-native-paper';
import { StatusBar } from 'expo-status-bar';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useOnboarding } from '@/src/context/OnboardingContext';
import { Ionicons } from '@expo/vector-icons';
import { LoadingScreen } from '@/src/components/LoadingScreen';

/**
 * Legacy employment step (support 5781 correction).
 *
 * This screen MUST NOT own a second company-association/payer flow. The
 * canonical registration journey (`/register/driver`) owns company
 * confirmation -> EXPLICIT payer -> association, and the previous arrangement
 * here associated WITHOUT a payer, so the backend required-field validation
 * rejected the call (400) and nothing was saved (D-031/D-053). It also treated
 * ANY HTTP success as `hasEmployment` and pushed /onboarding/vehicle regardless
 * of the real pending company / no-invitation / lead / self-pay outcome.
 *
 * Behaviour now:
 *   - an already-resolved employment keeps its previous Continue behaviour
 *     (-> /onboarding/vehicle) with NO payer and NO association;
 *   - no resolved employment, or an association edit, redirects to the
 *     canonical registration journey.
 */
export default function EmploymentScreen() {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const { state, fetchEmploymentInfo } = useOnboarding();

  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  // The redirect must only be decided once the authoritative fetch has settled:
  // redirecting on the initial empty state would bounce a driver who DOES have
  // an employment before their employment data loads.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadEmploymentData();
  }, [retryCount]);

  const loadEmploymentData = async () => {
    setLoaded(false);
    try {
      setError(null);
      await fetchEmploymentInfo();
    } catch (err) {
      console.error('Error fetching employment info:', err);
      const fallbackMessage = t('networkError', 'Não foi possível carregar os dados. Verifique sua conexão.');

      if (err instanceof Error) {
        const normalized = err.message.toLowerCase();
        const isRawNetworkError =
          normalized.includes('network request failed') ||
          normalized.includes('failed to fetch');
        const isOnboardingNetworkToken = err.message === 'ONBOARDING_EMPLOYMENT_NETWORK_ERROR';

        if (isRawNetworkError || isOnboardingNetworkToken) {
          setError(fallbackMessage);
          return;
        }

        setError(err.message);
        return;
      }

      setError(fallbackMessage);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!loaded || error || state.isLoading.employment) return;
    // No resolved employment -> the canonical registration journey owns the
    // company declaration and the explicit payer (D-031/D-053). Never a second
    // association flow here.
    if (!state.employmentInfo) {
      router.replace('/register/driver');
    }
  }, [loaded, error, state.isLoading.employment, state.employmentInfo, router]);

  const handleContinue = () => {
    // Existing, already-resolved employment: unchanged continue behaviour.
    router.push('/onboarding/vehicle');
  };

  const handleEdit = () => {
    // Editing the association belongs to the canonical journey (explicit payer),
    // never to a local payer-less association form.
    router.replace('/register/driver');
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <Ionicons name="cloud-offline-outline" size={48} color={theme.colors.error} />
      <Text style={[styles.errorText, { color: theme.colors.error }]}>
        {error}
      </Text>
      <Button
        mode="contained"
        onPress={handleRetry}
        style={styles.retryButton}
      >
        {t('retry', 'Tentar Novamente')}
      </Button>
    </View>
  );

  const employmentInfo = state.employmentInfo;
  const hasExistingCompany =
    !!employmentInfo &&
    employmentInfo.company_name !== 'Unknown Company' &&
    employmentInfo.company_nipc !== '';

  return (
    <Surface style={styles.container}>
      <StatusBar style="auto" />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.iconContainer}>
          <Ionicons name="business-outline" size={80} color={theme.colors.primary} />
        </View>

        <Text style={[styles.title, { color: theme.colors.primary }]}>
          {t('employmentTitle', 'Informação de Emprego')}
        </Text>

        {error ? (
          renderErrorState()
        ) : state.isLoading.employment || !employmentInfo ? (
          <LoadingScreen />
        ) : (
          <View style={styles.infoContainer}>
            <Text style={styles.infoText}>
              {t('employmentDataFound', 'Encontramos suas informações de emprego:')}
            </Text>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t('companyName', 'Nome da Empresa')}:</Text>
              <Text style={styles.infoValue}>{employmentInfo.company_name}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t('companyNipc', 'NIF/NIPC da Empresa')}:</Text>
              <Text style={styles.infoValue}>{employmentInfo.company_nipc}</Text>
            </View>
            {!!employmentInfo.company_email && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>{t('companyEmail', 'E-mail da Empresa')}:</Text>
                <Text style={styles.infoValue}>{String(employmentInfo.company_email)}</Text>
              </View>
            )}
            {!!employmentInfo.company_phone && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>{t('companyPhone', 'Telefone da Empresa')}:</Text>
                <Text style={styles.infoValue}>{String(employmentInfo.company_phone)}</Text>
              </View>
            )}
            {!hasExistingCompany && (
              <Button
                mode="outlined"
                onPress={handleEdit}
                style={styles.editButton}
              >
                {t('edit', 'Editar')}
              </Button>
            )}
          </View>
        )}

        <View style={styles.stepsContainer}>
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.primary }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
        </View>

        {!error && !!employmentInfo && (
          <Button
            mode="contained"
            style={styles.button}
            contentStyle={styles.buttonContent}
            onPress={handleContinue}
          >
            {t('continue', 'Continuar')}
          </Button>
        )}
      </ScrollView>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
  },
  iconContainer: {
    alignItems: 'center',
    marginVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  stepsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stepIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginHorizontal: 4,
  },
  button: {
    marginTop: 8,
  },
  buttonContent: {
    height: 48,
  },
  infoContainer: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
  },
  infoText: {
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  infoLabel: {
    fontWeight: 'bold',
    marginRight: 8,
    flex: 1,
  },
  infoValue: {
    flex: 2,
  },
  editButton: {
    marginTop: 8,
  },
  errorText: {
    fontSize: 12,
    marginTop: -12,
    marginBottom: 16,
    marginLeft: 12,
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    marginTop: 16,
  },
  retryButton: {
    marginTop: 16,
  },
});
